import { FormControl, FormGroup } from "@angular/forms";
import { HttpClient } from "@angular/common/http";
import { MatDialog } from "@angular/material/dialog";
import { GeoJSONDatabaseService } from "../../viewer-components/services/internal-services/geojson-database.service";
import { ViewerConfigurationService } from "../../viewer-components/services/viewer-configuration.service";
import { ChangeDetectorRef, Component, Input, inject } from "@angular/core";

@Component({
  template: "",
  standalone: false,
})
export abstract class VectorTileLayerComponent {
  protected readonly cdr = inject(ChangeDetectorRef);

  hide = true;

  isLoading: boolean = false;

  changed: boolean = true;

  urlTiles: string = null;

  maxZoom: number;

  minZoom: number;

  token: string;

  urlStyles: string = null;

  isAuthRequired: boolean;

  @Input()
  url: string;

  error: boolean = false;

  urlType: string;

  username: string;

  password: string;

  isArcGISAuth: boolean = false;

  form = new FormGroup({
    username: new FormControl(""),
    password: new FormControl(""),
  });

  protected constructor(
    protected http: HttpClient,
    protected viewerConfigurationService: ViewerConfigurationService,
    protected geoJSONDatabaseService: GeoJSONDatabaseService,
    protected matDialog: MatDialog,
  ) {}

  onAuthRequireChange(event: any) {
    this.isAuthRequired = event.value;
    this.changed = true;
    document.getElementById("lblError").innerHTML = "";
  }

  fieldsCompleted() {
    if (this.isAuthRequired) {
      return this.form.get("username").value !== "" && this.form.get("password").value !== "" && this.changed;
    }

    return this.changed;
  }

  changeInput() {
    this.changed = true;
  }

  usernameExists() {
    return this.form.get("username").value.length > 0;
  }

  passwordExists() {
    return this.form.get("password").value.length > 0;
  }

  getVectorTileServer() {
    this.isLoading = true;
    this.changed = false;
    document.getElementById("lblError").innerHTML = "";
    if (this.isAuthRequired && this.isArcGISAuth) {
      this.getTokenArcGISVectorTileServer();
    } else {
      this.getVectorTile();
    }
  }

  getTokenArcGISVectorTileServer() {
    const formData: any = new FormData();
    this.username = this.form.get("username").value;
    this.password = this.form.get("password").value;
    formData.append("username", this.username);
    formData.append("password", this.password);
    formData.append("referer", "https://www.arcgis.com");
    formData.append("f", "json");
    const url = "https://www.arcgis.com/sharing/rest/generateToken";
    this.http
      .post(url, formData)
      .toPromise()
      .then((res: any) => {
        this.token = res.token;
        this.getVectorTile();
      })
      .catch(() => {
        this.isLoading = false;
        this.error = true;
        this.cdr.detectChanges();
        document.getElementById("lblError").innerHTML = "Username or password wrong";
      });
  }

  getVectorTile() {
    let { url } = this;
    url = decodeURIComponent(url);

    const dataToSend = {
      url,
    };
    if (this.isTileUrl(url)) {
      this.getVectorTileSuccess({ dataUrl: url, defaultStyles: null });
    } else {
      if (this.token) url += `?token=${this.token}`;
      else if (this.isAuthRequired) {
        if (this.username && this.username.length) {
          // eslint-disable-next-line
            dataToSend["username"] = this.username;
        }
        if (this.password && this.password.length) {
          // eslint-disable-next-line
            dataToSend["password"] = this.password;
        }
      }
      this.http
        .get(url)
        .toPromise()
        .then((res: any) => {
          res.dataUrl = `${this.url}/${res.tiles[0]}`;
          this.getVectorTileSuccess(res);
        })
        .catch(() => {
          this.getVectorTileError();
        });
    }
  }

  getVectorTileSuccess(data: any) {
    if (data) {
      this.urlTiles = data.dataUrl ? data.dataUrl : null;
      this.urlStyles = data.defaultStyles ? `${this.url}/${data.defaultStyles}/` : null;
      this.maxZoom = data.maxzoom ? data.maxzoom : this.getMaxZoomByLods(data.tileInfo?.lods);
      this.minZoom = data.minzoom ? data.minzoom : this.getMinZoomByLods(data.tileInfo?.lods);
      if (!this.urlTiles) {
        this.isLoading = false;
        this.error = true;
        this.cdr.detectChanges();
        document.getElementById("lblError").innerHTML = "No layers were returned";
      } else {
        this.isLoading = false;
        this.changed = true;
        this.cdr.detectChanges();
        this.loadLayers();
        document.getElementById("lblError").innerHTML = "";
      }
    } else {
      this.isLoading = false;
      this.error = true;
      this.cdr.detectChanges();
      document.getElementById("lblError").innerHTML = "Bad data was returned";
    }
  }

  getVectorTileError() {
    this.isLoading = false;
    this.error = true;
    this.cdr.detectChanges();
    document.getElementById("lblError").innerHTML = "Failed to load url";
  }

  getMaxZoomByLods(lods: any[]): number {
    if (lods) return Math.max(...lods.map((l) => l.level));
    return null;
  }

  getMinZoomByLods(lods: any[]): number {
    if (lods) return Math.min(...lods.map((l) => l.level));
    return null;
  }

  isTileUrl(url: string): boolean {
    return url.includes("{x}") && url.includes("{y}") && url.includes("{z}");
  }

  abstract loadLayers();
}
